/**
 * Gift Cards Store Slice
 *
 * Zustand slice for gift card management.
 * Handles listing, creating, cancelling, and redeeming gift cards.
 */

import {
  clearError as clearOpError,
  hasOperationLoaded,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { giftCardsApi } from '@/api/giftCards';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';
import type {
  GiftCard,
  GiftCardWithMetadata,
  CreateGiftCardRequest,
  CreateGiftCardResponse,
  RedeemGiftCardResponse,
} from '@/types/giftCard.types';

import type { ErrorStates, ErrorStatuses, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface GiftCardsSlice {
  // State properties
  giftCards: GiftCardWithMetadata[];
  // Holds either the base GiftCard from the single-GET endpoint (fetchGiftCard)
  // or a GiftCardWithMetadata carried over from the list; the metadata fields
  // are optional here, so the wider GiftCardWithMetadata is the field type.
  currentGiftCard: GiftCardWithMetadata | null;
  giftCardsPagination: {
    nextToken?: string;
    hasMore: boolean;
  };
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Gift card listing actions
  fetchGiftCards: (orgId: string) => Promise<void>;
  fetchGiftCard: (orgId: string, giftCardId: string) => Promise<void>;
  loadMoreGiftCards: (orgId: string) => Promise<void>;

  // Gift card management actions
  createGiftCard: (
    orgId: string,
    request: CreateGiftCardRequest
  ) => Promise<CreateGiftCardResponse>;
  cancelGiftCard: (orgId: string, giftCardId: string) => Promise<void>;

  // Gift card redemption actions
  redeemGiftCard: (giftCardId: string) => Promise<RedeemGiftCardResponse>;

  // Utility actions
  clearGiftCardsError: (operation?: string) => void;
  clearCurrentGiftCard: () => void;
  resetGiftCards: () => void;
  checkIsLoading: (operation: string) => boolean;
  checkHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Initial State
// ============================================================================

const initialGiftCardsState = {
  giftCards: [],
  currentGiftCard: null,
  giftCardsPagination: {
    nextToken: undefined,
    hasMore: false,
  },
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createGiftCardsSlice: StateCreator<GiftCardsSlice> = (set, get) => ({
  ...initialGiftCardsState,

  // ============================================================================
  // Gift Card Listing Actions
  // ============================================================================

  /**
   * Fetch gift cards for an organization
   */
  fetchGiftCards: async (orgId: string) => {
    const operation = 'fetchGiftCards';
    if (isOperationLoading(get().isLoading, operation)) return;

    await createAsyncSliceAction(
      operation,
      () => giftCardsApi.listGiftCards(orgId),
      (response: { giftCards: GiftCardWithMetadata[]; nextToken?: string }) => ({
        giftCards: response.giftCards,
        giftCardsPagination: {
          nextToken: response.nextToken,
          hasMore: !!response.nextToken,
        },
      }),
      set,
      get
    )();
  },

  /**
   * Fetch a single gift card by ID
   */
  fetchGiftCard: async (orgId: string, giftCardId: string) => {
    const operation = 'fetchGiftCard';
    if (isOperationLoading(get().isLoading, operation)) return;

    await createAsyncSliceAction<GiftCardsSlice, GiftCard>(
      operation,
      () => giftCardsApi.getGiftCard(orgId, giftCardId),
      // The single-GET endpoint returns the base GiftCard shape without the
      // canRedeem/canCancel/daysRemaining metadata (see gift-cards.pact.spec.ts).
      // Those metadata fields are only populated for list-sourced cards, so the
      // base card is stored as-is; the cast reflects that they are absent here.
      (giftCard) => ({ currentGiftCard: giftCard as GiftCardWithMetadata }),
      set,
      get
    )();
  },

  /**
   * Load more gift cards (pagination)
   */
  loadMoreGiftCards: async (orgId: string) => {
    const operation = 'loadMoreGiftCards';
    const currentState = get();

    // Guard: Don't load more if no more pages or already loading
    if (!currentState.giftCardsPagination.hasMore) {
      return;
    }
    if (isOperationLoading(currentState.isLoading, operation)) {
      return;
    }

    set({
      isLoading: setLoading(currentState.isLoading, operation, true),
      errors: clearOpError(currentState.errors, operation),
    });

    try {
      const response = await giftCardsApi.listGiftCards(orgId, {
        nextToken: currentState.giftCardsPagination.nextToken,
      });

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, operation, true);

      set({
        giftCards: [...state.giftCards, ...response.giftCards],
        giftCardsPagination: {
          nextToken: response.nextToken,
          hasMore: !!response.nextToken,
        },
        isLoading: setLoading(state.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load more gift cards';
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, operation, false);

      set({
        isLoading: setLoading(state.isLoading, operation, false),
        errors: setError(state.errors, operation, errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Gift Card Management Actions
  // ============================================================================

  /**
   * Create a new gift card
   * Note: After creation, call fetchGiftCards to refresh the list
   */
  createGiftCard: async (orgId: string, request: CreateGiftCardRequest) => {
    const operation = 'createGiftCard';
    const state = get();

    set({
      isLoading: setLoading(state.isLoading, operation, true),
      errors: clearOpError(state.errors, operation),
    });

    try {
      const response = await giftCardsApi.createGiftCard(orgId, request);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );

      // Clear hasLoaded for fetchGiftCards to force refresh on next fetch
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: {
          ...loadedState.hasLoaded,
          fetchGiftCards: false,
        },
        lastLoaded: loadedState.lastLoaded,
      });

      return response;
    } catch (error) {
      const currentState = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to create gift card';
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        false
      );

      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(currentState.errors, operation, errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  /**
   * Cancel a gift card
   */
  cancelGiftCard: async (orgId: string, giftCardId: string) => {
    const operation = 'cancelGiftCard';
    const state = get();

    set({
      isLoading: setLoading(state.isLoading, operation, true),
      errors: clearOpError(state.errors, operation),
    });

    try {
      const cancelledGiftCard = await giftCardsApi.cancelGiftCard(orgId, giftCardId);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );

      // Update the gift card in the list, merging to preserve metadata fields
      set({
        giftCards: currentState.giftCards.map((gc) =>
          gc.giftCardId === giftCardId ? { ...gc, ...cancelledGiftCard, canCancel: false } : gc
        ),
        currentGiftCard:
          currentState.currentGiftCard?.giftCardId === giftCardId
            ? { ...currentState.currentGiftCard, ...cancelledGiftCard, canCancel: false }
            : currentState.currentGiftCard,
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const currentState = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel gift card';
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        false
      );

      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(currentState.errors, operation, errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Gift Card Redemption Actions
  // ============================================================================

  /**
   * Redeem a gift card using its ID
   */
  redeemGiftCard: async (giftCardId: string) => {
    const operation = 'redeemGiftCard';
    const state = get();

    set({
      isLoading: setLoading(state.isLoading, operation, true),
      errors: clearOpError(state.errors, operation),
    });

    try {
      const response = await giftCardsApi.redeemGiftCard({ giftCardId });

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );

      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });

      return response;
    } catch (error) {
      const currentState = get();
      const errorMessage = error instanceof Error ? error.message : 'Failed to redeem gift card';
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        false
      );

      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(currentState.errors, operation, errorMessage),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      throw error;
    }
  },

  // ============================================================================
  // Utility Actions
  // ============================================================================

  /**
   * Clear error for a specific operation or all errors
   */
  clearGiftCardsError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },

  /**
   * Clear current gift card
   */
  clearCurrentGiftCard: () => {
    set({ currentGiftCard: null });
  },

  /**
   * Reset gift cards state to initial values
   */
  resetGiftCards: () => {
    set(initialGiftCardsState);
  },

  /**
   * Check if an operation is loading
   */
  checkIsLoading: (operation: string) => isOperationLoading(get().isLoading, operation),

  /**
   * Check if an operation has loaded
   */
  checkHasLoaded: (operation: string) => hasOperationLoaded(get().hasLoaded, operation),
});
